import { Tooltip } from 'components/ui/tooltip';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import NMoreChip from 'components/util/NMoreChip';
import { ReactNode } from 'react';
import { cn } from 'utils/cn';

type ListClampProps<T> = {
    items: T[];
    renderItem: (item: T, index: number) => ReactNode;
    getKey: (item: T, index: number) => string | number;
    sort?: (a: T, b: T) => number;
    maxVisibleItems?: number;
    emptyContent?: ReactNode;
    maxTooltipHeight?: string | number;
    /** The count the "+N more" chip shows, for a list whose `items` hold only part of what exists. */
    moreCount?: number;
    /** Closes the hover below the items it lists, outside the scrolling area, so it stays in view. */
    tooltipFooter?: ReactNode;
    linkTo?: string;
    linkTarget?: string;
    className?: string;
};

// Small wrapper component so the render-prop is invoked from a component context
// rather than as an inline function call (satisfies react-doctor/no-render-in-render).
type RenderedItemProps<T> = {
    item: T;
    index: number;
    render: (item: T, index: number) => ReactNode;
};
function RenderedItem<T>({ item, index, render }: RenderedItemProps<T>) {
    return <>{render(item, index)}</>;
}

export default function ListClamp<T>({
    items,
    renderItem,
    getKey,
    sort,
    maxVisibleItems = 3,
    emptyContent = '',
    maxTooltipHeight,
    moreCount,
    tooltipFooter,
    linkTo,
    linkTarget,
    className,
}: ListClampProps<T>) {
    if ((!items || items.length === 0) && !((moreCount ?? 0) > 0)) {
        return <>{emptyContent}</>;
    }

    const processedItems = sort ? Array.from(items).toSorted(sort) : items;

    const visibleItems = processedItems.slice(0, maxVisibleItems);
    const remainingItems = processedItems.slice(maxVisibleItems);
    const chipCount = moreCount ?? remainingItems.length;

    return (
        <div className={cn('flex flex-col', className)}>
            {visibleItems.map((item, index) => (
                <div key={getKey(item, index)}>
                    <RenderedItem item={item} index={index} render={renderItem} />
                </div>
            ))}
            {chipCount > 0 && (
                <div>
                    <Tooltip
                        className="whitespace-normal"
                        content={
                            <div>
                                <div
                                    className={cn(maxTooltipHeight !== undefined && 'relative overflow-y-auto')}
                                    style={maxTooltipHeight !== undefined ? { maxHeight: maxTooltipHeight } : undefined}
                                >
                                    <div className="flex flex-col">
                                        {remainingItems.map((item, index) => (
                                            <div key={getKey(item, index + maxVisibleItems)}>
                                                <RenderedItem
                                                    item={item}
                                                    index={index + maxVisibleItems}
                                                    render={renderItem}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {tooltipFooter && <div className="mt-1">{tooltipFooter}</div>}
                            </div>
                        }
                    >
                        {linkTo ? (
                            <LinkWithOrigin to={linkTo} target={linkTarget} className="no-underline">
                                <NMoreChip count={chipCount} />
                            </LinkWithOrigin>
                        ) : (
                            <NMoreChip count={chipCount} />
                        )}
                    </Tooltip>
                </div>
            )}
        </div>
    );
}
